import googleIcon from '../../assets/images/LoginGoogleIcon.svg';
import {
  resetUserProfile,
  setUserProfile,
} from '../../redux/slices/userProfileSlice';
import { useCallback, useState } from 'react';
import { TokenResponse, useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginWithGoogle } from '../../Backend/services/authService';
import { LoginResponse } from '../../Backend/types';
import AuthButton from '../Auth/AuthButton';

interface GoogleButtonProps {
  text: string;
  imageUrl?: string;
  className?: string;
  onGoogleOnly?: (response: LoginResponse) => void;
  onError?: (message: string) => void;
}

const GoogleButton: React.FC<GoogleButtonProps> = ({ text, imageUrl, className = '', onGoogleOnly, onError }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  interface GoogleLoginSuccessResponse {
    access_token: string;
  }

  const handleLoginSuccess = useCallback(
    async (codeResponse: GoogleLoginSuccessResponse) => {
      try {
        setLoading(true);
        console.log('🔐 [GOOGLE-BUTTON] Iniciando login com Google...');
        const googleToken = codeResponse.access_token;

        // Buscar informações do utilizador do Google
        const googleUserInfoResponse = await axios.get(
          `https://www.googleapis.com/oauth2/v1/userinfo?alt=json&access_token=${googleToken}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${googleToken}`,
            },
          }
        );

        const { email, name, picture, id } = googleUserInfoResponse.data;
        console.log('📧 [GOOGLE-BUTTON] Email Google:', email);

        // Usar o novo serviço de autenticação V2
        const loginResponse = await loginWithGoogle({
          email,
          name,
          picture,
          id,
          google_id: id
        });

        if (loginResponse && loginResponse.user) {
          console.log('✅ [GOOGLE-BUTTON] Login bem-sucedido!');
          console.log('🔗 [GOOGLE-BUTTON] Account Linking:', loginResponse.account_linked);
          console.log('🆕 [GOOGLE-BUTTON] Novo utilizador:', loginResponse.is_new_user);

          // Normalizar dados do utilizador (garantir todos os campos opcionais têm valores)
          const normalizedUser = {
            ...loginResponse.user,
            username: loginResponse.user.username ?? '',
            biography: loginResponse.user.biography ?? '',
            club: loginResponse.user.club ?? '',
            date_of_birth: loginResponse.user.date_of_birth ?? null,
            phone_number: loginResponse.user.phone_number ?? '',
            location: loginResponse.user.location ?? '',
            local_play: loginResponse.user.local_play ?? '',
            profile_picture: loginResponse.user.profile_picture ?? '',
            gender: loginResponse.user.gender ?? '',
            subscription_type: loginResponse.user.subscription_type ?? 'free',
            status: loginResponse.user.status ?? 'active',
            date_joined: loginResponse.user.date_joined ?? new Date().toISOString(),
            subscription_expiration: loginResponse.user.subscription_expiration ?? '',
          };

          // Atualizar Redux com dados normalizados
          dispatch(setUserProfile(normalizedUser));
          localStorage.setItem('sessionRestored', 'true');

          const isGoogleOnly = loginResponse.login_method === 'google'
            || loginResponse.user.has_password === false;
          if (isGoogleOnly && onGoogleOnly) {
            onGoogleOnly(loginResponse);
            return;
          }

          // Redirecionar para última página visitada
          const lastVisitedPath = localStorage.getItem('lastVisitedPath') || '/';
          navigate(lastVisitedPath);
        } else {
          throw new Error('Login response inválida');
        }
      } catch (error) {
        console.error('❌ [GOOGLE-BUTTON] Erro no login Google:', error);
        dispatch(resetUserProfile());
        onError?.('Não foi possível entrar com o Google. Tenta novamente.');
      } finally {
        setLoading(false);
      }
    },
    [dispatch, navigate, onGoogleOnly, onError]
  );

  const handleLoginError = useCallback(
    (
      error: Pick<TokenResponse, 'error' | 'error_description' | 'error_uri'>
    ) => {
      console.error('Login failed:', error);
      dispatch(resetUserProfile());
      onError?.('Login com Google cancelado ou falhou.');
      setLoading(false);
    },
    [dispatch, onError]
  );

  const login = useGoogleLogin({
    onSuccess: handleLoginSuccess,
    onError: handleLoginError,
  });

  const handleLoginClick = useCallback(() => {
    login();
  }, [login]);
  return (
    <AuthButton
      type="button"
      variant="google"
      className={className}
      onClick={handleLoginClick}
      loading={loading}
      fullWidth
      aria-label={loading ? 'A abrir o Google' : text}
      leftIcon={<img src={imageUrl || googleIcon} alt="" width={20} height={20} />}
    >
      {loading ? 'A abrir o Google…' : text}
    </AuthButton>
  );
};

export default GoogleButton;
